import type HlsPlayer from "hls.js";
import { useEffect, useRef, useState } from "react";
import type { RoomRecord } from "../../shared/types";

export function VideoPreview({ room, onClose }: { room: RoomRecord; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState(() => room.hlsUrl ? "" : "抖音已确认开播，但没有返回可播放的 HLS 地址。");

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !room.hlsUrl) return;
    const showPlaybackError = () => setError("直播流加载失败，地址可能已过期或被跨域策略拦截。");
    video.addEventListener("error", showPlaybackError);
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = room.hlsUrl;
      void video.play().catch(showPlaybackError);
      return () => video.removeEventListener("error", showPlaybackError);
    }
    let cancelled = false;
    let player: HlsPlayer | null = null;
    void import("hls.js")
      .then(({ default: Hls }) => {
        if (cancelled) return;
        if (!Hls.isSupported()) {
          setError("当前浏览器不支持 HLS 播放。");
          return;
        }
        player = new Hls({ enableWorker: true, lowLatencyMode: true });
        player.loadSource(room.hlsUrl!);
        player.attachMedia(video);
        player.on(Hls.Events.MANIFEST_PARSED, () => void video.play().catch(() => undefined));
        player.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal || data.type === Hls.ErrorTypes.NETWORK_ERROR) showPlaybackError();
        });
      })
      .catch(() => setError("HLS 播放器加载失败，请打开抖音直播间。"));
    return () => {
      cancelled = true;
      video.removeEventListener("error", showPlaybackError);
      player?.destroy();
    };
  }, [room.hlsUrl]);

  return (
    <div className="modal-backdrop video-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="dialog video-dialog" role="dialog" aria-modal="true" aria-labelledby="video-title">
        <div className="dialog-head video-head">
          <div><p className="section-kicker">实时预览</p><h2 id="video-title">{room.alias || room.anchorName || room.webRid}</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <div className="video-stage">
          <video ref={videoRef} controls playsInline poster={room.coverUrl || undefined} />
          {error && <div className="video-error"><p>{error}</p><a className="button primary" href={room.originUrl} target="_blank" rel="noreferrer">打开抖音直播间</a></div>}
        </div>
        <p className="video-caption">{room.title || "抖音直播"} · 直播流由浏览器直连，LiveRadar 不转发视频。</p>
      </section>
    </div>
  );
}
